import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootReducerState } from '../../redux/Reducers';
import { rejectWithApiError } from '../../utils/api';
import { create, keyResolver, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import { BatchedOrgScopes, GlobalScope, OrgScope } from '../../types/authorization/authorization.types';

const orgScopesBatcher = create({
  fetcher: async (orgIds: number[]) => {
    if (orgIds.length === 1) {
      const response = await axios.get<OrgScope[]>(
        `${process.env.REACT_APP_API_URL}/authorization/org-scopes/${orgIds[0]}`
      );
      return [{ orgId: orgIds[0], scopes: response.data }];
    }

    const response = await axios.post<BatchedOrgScopes>(
      `${process.env.REACT_APP_API_URL}/authorization/org-scopes/batch`,
      { orgIds }
    );
    return response.data;
  },
  resolver: keyResolver('orgId'),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 10,
    maxBatchSize: 50,
  }),
});

export const fetchGlobalScopes = createAsyncThunk<
  GlobalScope[],
  void,
  { state: RootReducerState; rejectValue: string }
>(
  'authorization/fetchGlobalScopes',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<GlobalScope[]>(`${process.env.REACT_APP_API_URL}/authorization/global-scopes`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error });
    }
  },
  {
    condition: (_, { getState }) => !getState().authorizationReducer.globalScopesRequested,
  }
);

export const fetchOrgScopes = createAsyncThunk<
  OrgScope[],
  { orgId: number },
  { state: RootReducerState; rejectValue: string }
>(
  'authorization/fetchOrgScopes',
  async ({ orgId }, thunkAPI) => {
    try {
      const data = await orgScopesBatcher.fetch(orgId);
      return data.scopes;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error });
    }
  },
  {
    condition: ({ orgId }, { getState }) => !getState().authorizationReducer.orgScopesRequested[orgId],
  }
);
